import { Users, Plus, Trash2, Zap, Copy, Loader2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useMasters } from "./MastersContext";

const empty = { name: "", phone: "", email: "", role: "" };
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

export function PeopleSection() {
  const { people, addPerson, delPerson, scanDuplicates } = useMasters();
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [scanning, setScanning] = useState(false);
  const [dupInfo, setDupInfo] = useState(null);
  const rows = people.filter((p) => !q || [p.name, p.phone, p.email, p.role].some((v) => String(v || "").toLowerCase().includes(q.toLowerCase())));

  const weekAgo = Date.now() - WEEK_MS;
  const weekCount = people.filter((p) => p.created_at && new Date(p.created_at).getTime() >= weekAgo).length;

  async function add(e) {
    e.preventDefault();
    if (!form.name.trim()) return;
    await addPerson(form);
    setForm(empty);
  }

  async function checkDupes() {
    setScanning(true);
    try {
      const d = await scanDuplicates();
      const groups = d.people || [];
      setDupInfo({ groups: groups.length, records: groups.reduce((n, g) => n + g.length, 0) });
      if (!groups.length) toast.success("No duplicate people found — directory is clean.");
    } catch { toast.error("Scan failed"); }
    finally { setScanning(false); }
  }

  return (
    <section className="lg:col-span-2">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Users className="w-4 h-4 text-[#C84B31]" />
        <div className="section-title mb-0">People / Contacts Directory</div>
        <span className="text-[11px] text-[#68665E]">(auto-saved from LRs — drivers, POCs, contacts)</span>
        {weekCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#8A5A00] bg-[#FCEFCC] border border-[#F0DE9E] rounded-full px-2 py-0.5" data-testid="people-week-chip">
            <Zap className="w-3 h-3" /> {weekCount} learned this week
          </span>
        )}
        <button onClick={checkDupes} disabled={scanning} className="btn-secondary text-xs ml-auto" data-testid="people-dupe-check">
          {scanning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />} Review possible duplicates
        </button>
      </div>
      {dupInfo && dupInfo.groups > 0 && (
        <button
          onClick={() => document.getElementById("dupe-section")?.scrollIntoView({ behavior: "smooth" })}
          className="w-full text-left text-sm text-[#8A2526] bg-[#FBEAEA] border border-[#F0C9C9] rounded px-3 py-2 mb-3 hover:bg-[#F7DEDE] transition-colors"
          data-testid="people-dupe-banner"
        >
          Found <strong>{dupInfo.groups}</strong> possible duplicate group{dupInfo.groups > 1 ? "s" : ""} ({dupInfo.records} people) — click to review &amp; merge below.
        </button>
      )}
      <form onSubmit={add} className="card-surface p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="input-field" placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="person-name-input" />
          <input className="input-field" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="person-phone-input" />
          <input className="input-field" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="person-email-input" />
          <input className="input-field" placeholder="Role (e.g. Driver, POC)" list="person-roles" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="person-role-input" />
          <datalist id="person-roles">
            {["Driver", "Helper", "Pickup POC", "Delivery POC", "Hirer Contact", "Manager", "Owner", "Accounts", "Contact"].map((r) => <option key={r} value={r} />)}
          </datalist>
        </div>
        <button type="submit" className="btn-primary mt-3" data-testid="add-person-button"><Plus className="w-4 h-4" /> Add Person</button>
      </form>
      <div className="table-wrap">
        <input className="input-field w-64 mb-3" placeholder="Search people..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="person-search" />
        <table className="w-full">
          <thead><tr><th className="table-th">Name</th><th className="table-th">Phone</th><th className="table-th">Email</th><th className="table-th">Role</th><th className="table-th text-right"></th></tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td className="table-td text-[#68665E] text-center" colSpan={5}>{q ? "No matching people." : "No people saved yet — they build up automatically from LRs."}</td></tr>
            ) : rows.map((p) => (
              <tr key={p.id} className="table-row" data-testid={`person-row-${p.id}`}>
                <td className="table-td font-medium">{p.name}</td>
                <td className="table-td">{p.phone || "—"}</td>
                <td className="table-td text-[#68665E]">{p.email || "—"}</td>
                <td className="table-td">{p.role || "—"}</td>
                <td className="table-td text-right"><button className="btn-ghost text-[#8A2526]" onClick={() => delPerson(p.id)} data-testid={`person-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
